<template>
  <div class="due-date-rules-container">
    <b-alert
      show
      variant="primary"
    >
      <div class="alert-body d-flex align-items-center">
        <div>
          <feather-icon
            class="mr-50"
            icon="InfoIcon"
            size="22"
          />
        </div>
        <div class="ml-25">
          <strong>Regra de cálculo de faturamento/recebimento</strong>
          <div>Esta regra define quando um serviço prestado (ex.: plantão) deve ser faturado ou recebido com base na data em que o serviço foi realizado.</div>
        </div>
      </div>
    </b-alert>

    <b-card no-body>
      <b-card-body class="d-flex flex-column flex-xl-row justify-content-between align-items-center pb-2">
        <div class="">
          <h4>Regra de faturamento/recebimento</h4>
          <div class="d-flex w-100">
            <div class="mr-1">
              <label for="due">Dia fixo para faturar/receber</label>
              <b-form-group :state="dueDaySelectState()">
                <b-form-select
                  id="due"
                  v-model="workplaceDueDateRules.due_day"
                  :options="daysOfMonthOptions"
                  size="sm"
                  :state="dueDaySelectState()"
                />
                <b-form-invalid-feedback :state="dueDaySelectState()">
                  Campo obrigatório
                </b-form-invalid-feedback>
              </b-form-group>
            </div>
          </div>

          <h5 class="mt-2">
            Faixas de dias do serviço
          </h5>
          <div class="w-100">
            <div>
              <b-table
                responsive="sm"
                :fields="tableColumns"
                :items="workplaceDueDateRules.rules"
              >
                <template #cell(start_day)="{item}">
                  <b-form-group :state="ruleSelectState(item, 'start_day')">
                    <b-form-select
                      v-model="item.start_day"
                      :options="disableGreater(daysOfMonthOptions, item.end_day)"
                      size="sm"
                      :state="ruleSelectState(item, 'start_day')"
                    />
                    <b-form-invalid-feedback :state="ruleSelectState(item, 'start_day')">
                      {{ ruleFieldFeedback(item, 'start_day') }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </template>
                <template #cell(end_day)="{item}">
                  <b-form-group :state="ruleSelectState(item, 'end_day')">
                    <b-form-select
                      v-model="item.end_day"
                      :options="disableLess(daysOfMonthOptions, item.start_day)"
                      size="sm"
                      :state="ruleSelectState(item, 'end_day')"
                    />
                    <b-form-invalid-feedback :state="ruleSelectState(item, 'end_day')">
                      {{ ruleFieldFeedback(item, 'end_day') }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </template>
                <template #cell(months_to_add)="{item}">
                  <b-form-group :state="ruleSelectState(item, 'months_to_add')">
                    <b-form-select
                      v-model="item.months_to_add"
                      :options="addMonthOptions"
                      size="sm"
                      :state="ruleSelectState(item, 'months_to_add')"
                    />
                    <b-form-invalid-feedback :state="ruleSelectState(item, 'months_to_add')">
                      Campo obrigatório
                    </b-form-invalid-feedback>
                  </b-form-group>
                </template>
                <template #cell(result)="{item}">
                  <div v-if="item.months_to_add">
                    Dia {{ workplaceDueDateRules.due_day }} após {{ item.months_to_add }} meses
                  </div>
                  <div
                    v-else
                    class="text-center"
                  >
                    -
                  </div>
                </template>
                <template #cell(actions)="{item}">
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="deleteRule(item)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                    />
                  </b-button>
                </template>
              </b-table>
            </div>
            <div class="ml-2">
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-primary"
                size="sm"
                @click="addDueDateRule"
              >
                <feather-icon
                  icon="PlusIcon"
                />
                Adicionar faixa
              </b-button>
            </div>
          </div>
        </div>

        <b-alert
          class="mt-1 ml-1 mt-xl-0"
          show
          variant="primary"
        >
          <div class="alert-body">
            <div>
              <feather-icon
                class="mr-50"
                icon="InfoIcon"
                size="22"
              />
              <strong>Como funciona</strong>
            </div>
            <div class="mt-1">
              O sistema verifica o dia do mês do serviço prestado, encontra a
              faixa correspondente e calcula a data final usando o dia fixo configurado.
            </div>
            <div class="mt-1">
              <strong>Exemplo:</strong>
              <p>
                Serviço realizado em 21/02/2026 (dia 21)
                <ul class="m-0">
                  <li>Faixa aplicada: 20 a 31 ➔ somar 3 meses</li>
                  <li>Resultado: 15/05/2026</li>
                </ul>
                Serviço realizado em 03/05/2026 (dia 3)
                <ul class="m-0">
                  <li>Faixa aplicada: 1 a 19 ➔ somar 2 meses</li>
                  <li>Resultado: 15/07/2026</li>
                </ul>
              </p>
            </div>
          </div>
        </b-alert>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <div class="">
          <h4 class="-mt-2">
            Simulação de cálculo
          </h4>
          <p>Veja exemplos de como o cálculo será aplicado a diferentes datas de serviço.</p>
          <div>
            <b-table
              responsive="sm"
              :fields="tableSimulatorColumns"
              :items="workplaceDueDateRulesSimulations"
            >
              <template #cell(duty_date)="{item}">
                {{ formatDateTimeDDMMYYYY(item.duty_date) }}
              </template>
              <template #cell(day_range)="{item}">
                {{ item.day_range.start }} a {{ item.day_range.end }}
              </template>
              <template #cell(months_to_add)="{item}">
                +{{ item.months_to_add }} meses
              </template>
              <template #cell(date)="{item}">
                {{ formatDateTimeDDMMYYYY(item.date) }}
              </template>
            </b-table>
          </div>
          <p>ⓘ Se o dia fixo (15) não existir no mês calculado, será utilizado o último dia do mês.</p>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BFormGroup,
  BFormInvalidFeedback,
  BFormSelect,
  BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDateTimeDDMMYYYY } from '@core/utils/utils'
import useWorkplaceDueDateRules from './useWorkplaceDueDateRules'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BFormGroup,
    BFormInvalidFeedback,
    BFormSelect,
    BTable,
  },
  directives: {
    Ripple,
  },
  setup() {
    const toast = useToast()

    const {
      workplaceDueDateRules,
      workplaceDueDateRulesSimulations,
      daysOfMonthOptions,
      addMonthOptions,
      tableColumns,
      tableSimulatorColumns,
      dueDaySelectState,
      ruleSelectState,
      ruleFieldFeedback,
      loadWorkplace,
      validateDueDateRulesBeforeSave,
      getPayload,
      addDueDateRule,
      deleteRule,
      disableLess,
      disableGreater,
    } = useWorkplaceDueDateRules()

    function validate() {
      const validationError = validateDueDateRulesBeforeSave()
      if (validationError) {
        toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Validação',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: validationError,
          },
        })
        return false
      }
      return true
    }

    return {
      workplaceDueDateRules,
      workplaceDueDateRulesSimulations,
      daysOfMonthOptions,
      addMonthOptions,
      tableColumns,
      tableSimulatorColumns,
      dueDaySelectState,
      ruleSelectState,
      ruleFieldFeedback,
      loadWorkplace,
      validate,
      validateDueDateRulesBeforeSave,
      getPayload,
      addDueDateRule,
      deleteRule,
      disableLess,
      disableGreater,
      formatDateTimeDDMMYYYY,
    }
  },
}
</script>

<style lang="scss">
.alert-body {
  font-size: 12px;
}
</style>
